import { useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowUpFromLine, Bot, Minus, Check, CloudOff, Eye, EyeOff, GitFork, GripVertical, Hand, LayoutDashboard, ListChecks, MoreHorizontal, Play, Plus, RotateCw, Settings2, Sparkles, X } from 'lucide-react';
import { SUBTASK_CONCURRENCY_MAX, type SubtaskOrderChange, type SubtaskOrderSuggestion, type TaskCard, type TaskColumn, type TaskLabel, type TaskMember, type TaskType } from '@shared/types/tasks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { AppAlert } from '@/components/AppAlert';
import { orderSuggestionCount } from '@shared/subtask-order';
import { OrphanedBadge, SubtaskAfter } from './SubtaskOf';
import { useFormatTaskRef } from '@/lib/current-project';
import { useBoardPath } from '@/navigation/project-paths';
import { cn } from '@/lib/utils';
import { chainLeftToBuild, hasSubtaskGraph, isOrderSuggestionStale, isSubtaskBuilt, isSubtaskDone, isSubtaskUnlocked, newOrderEdges, reachedBreaks, suggestedPositions, withOrderSuggestion, subtaskBefore, subtaskLanes, subtasksToBuild, waitingOn, waitsOnAi } from './subtasks';
import { SubtaskGraph } from './SubtaskGraph';
import type { PendingSubtaskRow } from './pending-subtasks';
import { fromParentState } from './subtask-return';
import { Spinner } from '@/components/ui/spinner';
import { TaskCardChips, TaskCardCounts, TaskCardPeople } from './TaskCardMeta';
import { taskCardFields, useTaskTurn } from './task-card-meta';
import { TaskCardPrs } from './TaskCardPrs';
import { PickupSkipBadge, RunLimitBadge } from './PickupSkip';
import { pickupSkipOf } from './task-waiting';
import { RunProgressBar } from '@/components/factory/RunProgressBar';
import { TaskActiveRunsContext, useTaskActiveRun } from '@/components/factory/active-runs';

export interface TaskSubtasksProps {
  subtasks: TaskCard[];
  columns: TaskColumn[];
  /** The Board's people, labels and types, for each open sub-task's details line (T-92). */
  members: TaskMember[];
  labels: TaskLabel[];
  types?: TaskType[];
  /** Maintainers: hand sub-tasks to the AI builder and build a picked set in one run. */
  canRun: boolean;
  /**
   * SF-T-144: sub-tasks added here the server doesn't have yet (saving, or failed), after the list.
   * Adding one shows it at once and empties the box.
   */
  pending?: PendingSubtaskRow[];
  onAdd: (title: string) => void;
  onRetryPending?: (row: PendingSubtaskRow) => void;
  onRemovePending?: (id: string) => void;
  /** SF-T-147: make it a task of its own, off this parent (the ⋯ menu); people who can edit tasks. */
  onPromote?: (task: TaskCard) => void;
  /** Ticked: to the first done column; unticked: back to the first that isn't. */
  onToggleDone: (task: TaskCard, done: boolean) => void;
  /** Waiting on: AI builder, so it's picked up and built in a run of its own. */
  onHandToAi: (task: TaskCard) => void;
  onToggleBoard: (task: TaskCard, show: boolean) => void;
  /** Build these together, in the list's order: one run, one thread, one branch and one PR (opens the Run dialog). */
  onBuild: (ids: string[]) => void;
  /** Dragged into a new order: every sub-task's id, in it. */
  onReorder: (ids: string[]) => void;
  /**
   * SF-T-242: how the AI builder builds them: one at a time (each its own run and PR, stacked on the one before)
   * and without stopping for questions. Changed from the header's settings menu (onChangeBuild, Maintainers).
   */
  build?: SubtaskBuild;
  onChangeBuild?: (change: Partial<SubtaskBuild>) => void;
  /** SF-T-242: one at a time, Build hands the parent to the AI builder (the pick-up starts the first); true once it has. */
  onBuildOneAtATime?: () => void;
  buildingOneAtATime?: boolean;
  /**
   * SF-T-263: one at a time, Pick… sets the chain: the picked ones (replacing the last pick), built one by one,
   * and the parent handed to the AI builder. The chain stops once each has a PR.
   */
  onPickChain?: (ids: string[]) => void;
  /** SF-T-263: clear the chain's pick (the settings menu): the AI builder hands no more over. */
  onClearChain?: () => void;
  /**
   * SF-T-268: what a sub-task waits on (task.after: other sub-tasks of this task), from its After… picker; people who
   * can edit tasks. A one-at-a-time parent hands it to the AI builder once each has a PR or is done. Resolves whether
   * it saved: the picker stays open, with its ticks, until it has.
   */
  onChangeAfter?: (task: TaskCard, ids: string[], was?: string[]) => Promise<boolean>;
  /**
   * SF-T-269: "Stop here for a person" on a sub-task of a one-at-a-time parent (task.chain_break): once it has its PR,
   * the chain hands over nothing that waits on it. People who can edit tasks.
   */
  onToggleBreak?: (task: TaskCard, on: boolean) => void;
  /**
   * SF-T-269: the chain stopped at a break (the parent's subtasks_chain_stopped_for): Continue hands the parent back to
   * the AI builder, past it (Maintainers).
   */
  chainStopped?: boolean;
  onContinue?: () => void;
  /**
   * SF-T-271: Suggest the order (onSuggestOrder, people who can edit tasks): the AI proposes which sub-tasks wait on
   * which, and breaks. The suggestion (the parent's subtasks_suggestion, also made from a chat or a run) shows as a
   * banner with Accept (onAcceptOrder: every change in one go) and Dismiss, and each changed row says what it would
   * change. Once the sub-tasks change, it says so and offers to suggest again.
   */
  suggestion?: SubtaskOrderSuggestion | null;
  suggestingOrder?: boolean;
  onSuggestOrder?: () => void;
  onAcceptOrder?: () => void;
  onDismissOrder?: () => void;
}

/** SF-T-242: a parent's settings for how the AI builder builds its sub-tasks (task.subtasks_*). */
export interface SubtaskBuild {
  subtasks_one_at_a_time: boolean;
  subtasks_defer_questions: boolean;
  /**
   * SF-T-267: how many of them the AI builder has at once, one at a time (1 to SUBTASK_CONCURRENCY_MAX). More than 1
   * only matters once some wait on others (After…): without, each waits on the one before it.
   */
  subtasks_concurrency?: number;
}

/**
 * T-42: a task's sub-tasks, as a checklist. Each one is a task of its own, off the Board unless shown
 * there; ticking moves it to Done. Under each open one's title, the Board card's details (T-92,
 * TaskCardMeta): chips, labels, whose turn it is, PRs, a run going, counts and assignees. Maintainers can hand one to the AI builder, or pick several and
 * build them together in one run. Drag the handle to put them in order (the order a batch is briefed in).
 */
export function TaskSubtasks({
  subtasks,
  columns,
  members,
  labels,
  types,
  canRun,
  pending = [],
  onAdd,
  onRetryPending,
  onRemovePending,
  onPromote,
  onToggleDone,
  onHandToAi,
  onToggleBoard,
  onBuild,
  onReorder,
  build,
  onChangeBuild,
  onBuildOneAtATime,
  buildingOneAtATime = false,
  onPickChain,
  onClearChain,
  onChangeAfter,
  onToggleBreak,
  chainStopped,
  onContinue,
  suggestion,
  suggestingOrder = false,
  onSuggestOrder,
  onAcceptOrder,
  onDismissOrder,
}: TaskSubtasksProps) {
  const formatRef = useFormatTaskRef();
  const oneAtATime = Boolean(build?.subtasks_one_at_a_time && onBuildOneAtATime);
  // SF-T-263: Pick… picks the chain rather than a batch.
  const pickChain = oneAtATime ? onPickChain : undefined;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [title, setTitle] = useState('');
  // SF-T-305: with none, only "Add sub-tasks", as with a spec; pressing it opens the section and its box.
  const [adding, setAdding] = useState(false);
  const addBox = useRef<HTMLInputElement>(null);
  // Esc with nothing typed folds it back. The task pop-up (a Radix dialog) hears Escape on the document before
  // the box does, so it's caught on the window first, as TaskSpec and MentionTextarea do.
  const foldable = adding && !subtasks.length && !pending.length;
  const fold = useEffectEvent(() => setAdding(false));
  useEffect(() => {
    if (!foldable) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.target !== addBox.current || addBox.current?.value) return;
      e.preventDefault();
      e.stopPropagation();
      fold();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [foldable]);
  // Picking sub-tasks for one run: null while not picking.
  const [picked, setPicked] = useState<string[] | null>(null);
  const done = subtasks.filter((t) => isSubtaskDone(t, columns)).length;
  const open = subtasks.filter((t) => !isSubtaskDone(t, columns));
  const chainLeft = chainLeftToBuild(subtasks, columns);
  // SF-T-268: the dependency graph, once one waits on another: drawn beside the list when there's room; narrower, each
  // row's 'after …' says it.
  const graph = hasSubtaskGraph(subtasks);
  const lanes = subtaskLanes(subtasks);
  // SF-T-269: the pick-up stopped the chain at a break, and it isn't with the AI builder again yet: the breaks it reached.
  const stopped = Boolean(oneAtATime && chainStopped && !buildingOneAtATime);
  const stoppedAt = stopped ? reachedBreaks(subtasks, columns) : [];
  const beforeInOrder = subtaskBefore(subtasks);
  // SF-T-279: the ones ready for what waits on them: built, and nothing working on them now (the Board's live runs too).
  const activeRuns = useContext(TaskActiveRunsContext);
  const unlocked = useMemo(
    () => new Set(subtasks.filter((s) => isSubtaskUnlocked({ ...s, run_going: s.run_going || activeRuns.has(s.id) }, columns)).map((s) => s.id)),
    [subtasks, columns, activeRuns]
  );
  // SF-T-301: the open ones still to build: no PR yet, and nothing building them now.
  const toBuild = subtasksToBuild(open, columns, activeRuns);
  const someUnbuilt = open.some((t) => !isSubtaskBuilt(t, columns));
  // Pick… opens straight away as Build with AI only when it's the one sub-task left to build.
  const pickOnly = open.length > 1 || oneAtATime || !toBuild.length;
  // SF-T-271: the AI's suggested order: its changes show on the rows while it's still for the sub-tasks as they are.
  const suggestionStale = suggestion ? isOrderSuggestionStale(suggestion, subtasks, columns, Boolean(build?.subtasks_one_at_a_time)) : false;
  const live = suggestion && !suggestionStale ? suggestion : null;
  const suggested = new Map(live ? live.changes.map((c) => [c.task_id, c]) : []);
  const moved = live ? suggestedPositions(live, subtasks) : new Map<string, number>();
  // SF-T-271: the graph as the suggestion would make it, drawn in place of today's with what it adds in its colour.
  const suggestedCards = live ? withOrderSuggestion(subtasks, live) : null;
  const suggestedGraph = suggestedCards && hasSubtaskGraph(suggestedCards) ? suggestedCards : null;
  const showGraph = graph || Boolean(suggestedGraph);
  // As the server counts them: two or more still to build (open, not a draft, no PR that isn't closed).
  const canSuggest = Boolean(onSuggestOrder) && open.filter((t) => !t.draft && !t.archived && !t.prs.some((p) => p.status !== 'closed')).length > 1;
  const add = (e: FormEvent) => {
    e.preventDefault();
    const text = title.trim();
    if (!text) return;
    onAdd(text);
    setTitle('');
  };

  const togglePick = (id: string, on: boolean) =>
    setPicked((prev) => (on ? [...(prev ?? []), id] : (prev ?? []).filter((p) => p !== id)));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = subtasks.findIndex((t) => t.id === active.id);
    const to = subtasks.findIndex((t) => t.id === over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(subtasks, from, to).map((t) => t.id));
  };

  if (!subtasks.length && !pending.length && !adding) {
    return (
      <div>
        <Button type="button" variant="ghost" size="xs" className="text-muted-foreground max-md:h-11" onClick={() => setAdding(true)}>
          <Plus className="size-3.5" />
          Add sub-tasks
        </Button>
      </div>
    );
  }

  return (
    <section className="@container/subtasks space-y-2" aria-label="Sub-tasks">
      <div className="flex min-h-7 items-center gap-2">
        <h3 className="text-sm font-semibold whitespace-nowrap">Sub-tasks</h3>
        {subtasks.length ? (
          <span className="text-xs tabular-nums text-muted-foreground" data-hint={`${done} of ${subtasks.length} done`}>
            {done}/{subtasks.length}
          </span>
        ) : null}
        <span className="flex-1" />
        {canSuggest && !picked ? (
          <Button
            variant="ghost"
            size="xs"
            loading={suggestingOrder}
            onClick={onSuggestOrder}
            aria-label="Suggest the order"
            data-hint="The AI suggests which sub-tasks wait on which, and where to stop for a person. Nothing changes until you accept it"
          >
            <Sparkles />
            {/* Phones: the icon only, so the header keeps one line. */}
            <span className="hidden sm:inline">Suggest the order</span>
          </Button>
        ) : null}
        {canRun && build && onChangeBuild ? <SubtaskBuildMenu
            build={build}
            onChange={onChangeBuild}
            chainLeft={chainLeft}
            onClearChain={onClearChain && subtasks.some((t) => t.in_chain) ? onClearChain : undefined}
          /> : null}
        {canRun && open.length ? (
          picked ? (
            <>
              <Button variant="ghost" size="xs" onClick={() => setPicked(null)}>
                Cancel
              </Button>
              <Button
                size="xs"
                disabled={!picked.length}
                disabledReason={pickChain ? 'Pick the sub-tasks to build one at a time' : 'Pick the sub-tasks to build together'}
                data-hint={pickChain ? 'The AI builder builds these in order, one at a time, and stops after the last' : undefined}
                onClick={() => {
                  const ids = subtasks.filter((t) => picked.includes(t.id)).map((t) => t.id);
                  if (pickChain) pickChain(ids);
                  else onBuild(ids);
                  setPicked(null);
                }}
              >
                <Play />
                {pickChain ? `Build ${picked.length || ''} one at a time` : `Build ${picked.length || ''} with AI…`}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant={pickOnly ? 'ghost' : 'outline'}
                size="xs"
                // SF-T-263: one at a time, starting from the chain as it is.
                onClick={() => setPicked(pickChain ? open.filter((t) => t.in_chain).map((t) => t.id) : [])}
                data-hint={
                  pickChain
                    ? 'Pick the sub-tasks to build one at a time: each its own run and PR. The AI builder stops after the last'
                    : 'Pick sub-tasks for one agent to build together: one run, one branch, one PR'
                }
              >
                <ListChecks />
                {pickOnly ? 'Pick…' : 'Build with AI'}
              </Button>
              {/* SF-T-242: one at a time, the AI builder takes the parent and starts the first. */}
              {oneAtATime ? (
                // While the AI builder has them, it says so: the chain keeps the parent until they're all done.
                toBuild.length || (buildingOneAtATime && open.length) ? (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={onBuildOneAtATime}
                  disabled={buildingOneAtATime}
                  disabledReason="The AI builder has them: each starts once the one before has a PR"
                  data-hint="The AI builder builds them in order, one at a time: each its own run and PR, stacked on the one before"
                >
                  <Play />
                  {/* Phones: short, so the header keeps one line. */}
                  <span className="sm:hidden">{buildingOneAtATime ? 'With AI' : 'One at a time'}</span>
                  <span className="hidden sm:inline">{buildingOneAtATime ? 'With the AI builder' : 'Build one at a time with AI'}</span>
                </Button>
                ) : null
              ) : /* SF-T-103: a merged task's sub-tasks, all of them in one go. SF-T-301: those still to build. */
              // While the rest are being built, it stays, disabled.
              open.length > 1 && someUnbuilt ? (
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!toBuild.length}
                  disabledReason="They're being built now"
                  onClick={() => onBuild(toBuild.map((t) => t.id))}
                  data-hint="One agent builds every open sub-task that has no PR and isn't being built: one run, one branch, one PR"
                >
                  <Play />
                  {!toBuild.length
                    ? 'Being built'
                    : toBuild.length === open.length
                    ? `Build all ${toBuild.length} with AI…`
                    : toBuild.length === 1
                      ? 'Build the one left with AI…'
                      : `Build the ${toBuild.length} left with AI…`}
                </Button>
              ) : null}
            </>
          )
        ) : null}
      </div>
      {/* SF-T-242: how the AI builder builds them, when it isn't all in one batch, stopping to ask. */}
      {build?.subtasks_one_at_a_time || build?.subtasks_defer_questions ? (
        <p className="-mt-1 text-2xs text-muted-foreground">
          {[
            build.subtasks_one_at_a_time
              ? chainLeft
                ? `The AI builder builds the picked ones one at a time (${chainLeft} left), each its own PR stacked on the one before`
                : 'The AI builder builds them one at a time, each its own PR stacked on the one before'
              : null,
            build.subtasks_defer_questions ? (build.subtasks_one_at_a_time ? "without stopping for questions" : "The AI builder's runs don't stop for questions") : null,
          ]
            .filter(Boolean)
            .join(', ')}
          .
        </p>
      ) : null}

      {stopped ? (
        <AppAlert
          tone="warning"
          compact
          icon={<Hand className="size-4" />}
          data-testid="chain-stopped"
          action={
            onContinue ? (
              <Button variant="outline" size="xs" onClick={onContinue} data-hint="The AI builder takes this task again and hands over what waits on the break">
                <Play />
                Continue
              </Button>
            ) : undefined
          }
        >
          {stoppedAt.length
            ? `The chain stopped for a person at ${stoppedAt.map((t) => formatRef(t.number)).join(', ')}. Look it over, then go on.`
            : 'The chain stopped for a person at a break. Look it over, then go on.'}
        </AppAlert>
      ) : null}

      {suggestion ? (
        <SubtaskOrderAlert
          suggestion={suggestion}
          stale={suggestionStale}
          suggesting={suggestingOrder}
          onAccept={onAcceptOrder}
          onDismiss={onDismissOrder}
          onSuggestAgain={canSuggest ? onSuggestOrder : undefined}
        />
      ) : null}
      {/* SF-T-271: narrower (phones), the suggested graph sits under the banner (sideways it scrolls); wide, it's beside the list. */}
      {suggestedGraph ? (
        <SubtaskGraph
          subtasks={suggestedGraph}
          columns={columns}
          lanes={subtaskLanes(suggestedGraph)}
          suggested={{ edges: newOrderEdges(subtasks, suggestedGraph), nodes: new Set([...suggested.keys(), ...moved.keys()]) }}
          className="rounded-md border border-ai-border p-2 @3xl/subtasks:hidden"
        />
      ) : null}

      {subtasks.length ? (
        <>
          {/* SF-T-279: narrower (phones), the steps sit above the list, folded to their key; wide, they're beside it. */}
          {graph && !suggestedGraph ? <SubtaskGraph subtasks={subtasks} columns={columns} lanes={lanes} foldable className="mb-2 @3xl/subtasks:hidden" /> : null}
          <div className={cn(showGraph && '@3xl/subtasks:flex @3xl/subtasks:items-start @3xl/subtasks:gap-4')}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={subtasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                <ul className="min-w-0 flex-1 divide-y rounded-md border">
                  {subtasks.map((task) => (
                    <SubtaskRow
                      key={task.id}
                      task={task}
                      columns={columns}
                      members={members}
                      labels={labels}
                      types={types}
                      canRun={canRun}
                      sortable={subtasks.length > 1 && !picked}
                      picked={picked ? picked.includes(task.id) : null}
                      onPick={(on) => togglePick(task.id, on)}
                      onToggleDone={(on) => onToggleDone(task, on)}
                      onHandToAi={() => onHandToAi(task)}
                      onToggleBoard={() => onToggleBoard(task, !task.show_on_board)}
                      onPromote={onPromote ? () => onPromote(task) : undefined}
                      siblings={subtasks}
                      unlocked={unlocked}
                      showAfter={task.after.length > 0 && !(task.after.length === 1 && task.after[0].task_id === beforeInOrder.get(task.id))}
                      onChangeAfter={onChangeAfter && subtasks.length > 1 ? (ids, was) => onChangeAfter(task, ids, was) : undefined}
                      onToggleBreak={onToggleBreak && build?.subtasks_one_at_a_time ? (on) => onToggleBreak(task, on) : undefined}
                      stopped={stoppedAt.includes(task)}
                      suggested={suggested.get(task.id)}
                      suggestedAt={moved.get(task.id)}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            {suggestedGraph ? (
              <SubtaskGraph
                subtasks={suggestedGraph}
                columns={columns}
                lanes={subtaskLanes(suggestedGraph)}
                suggested={{ edges: newOrderEdges(subtasks, suggestedGraph), nodes: new Set([...suggested.keys(), ...moved.keys()]) }}
                className="hidden w-72 shrink-0 @3xl/subtasks:block"
              />
            ) : graph ? (
              <SubtaskGraph subtasks={subtasks} columns={columns} lanes={lanes} className="hidden w-72 shrink-0 @3xl/subtasks:block" />
            ) : null}
          </div>
        </>
      ) : null}

      {pending.length ? (
        <ul className="divide-y rounded-md border" aria-label="Sub-tasks not saved yet">
          {pending.map((row) => (
            <PendingRow key={row.id} row={row} onRetry={() => onRetryPending?.(row)} onRemove={() => onRemovePending?.(row.id)} />
          ))}
        </ul>
      ) : null}

      <form className="flex items-center gap-2" onSubmit={add}>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a sub-task"
          aria-label="New sub-task title"
          className="h-8"
          ref={addBox}
          // Opened from "Add sub-tasks": ready to type.
          autoFocus={foldable}
        />
        <Button type="submit" variant="outline" size="sm" disabled={!title.trim()}>
          <Plus />
          Add
        </Button>
      </form>
    </section>
  );
}

/** One sub-task: its tick, a handle to drag it, its link and its actions (or, while picking, a pick box). */
/**
 * SF-T-271: the AI's suggested order: how many sub-tasks it would change (each row says what), with Accept and Dismiss;
 * that it suggests no change; or, once the sub-tasks changed since, that it's out of date, with Suggest again.
 */
function SubtaskOrderAlert({
  suggestion,
  stale,
  suggesting,
  onAccept,
  onDismiss,
  onSuggestAgain,
}: {
  suggestion: SubtaskOrderSuggestion;
  stale: boolean;
  suggesting: boolean;
  onAccept?: () => void;
  onDismiss?: () => void;
  onSuggestAgain?: () => void;
}) {
  const n = orderSuggestionCount(suggestion);
  const formatRef = useFormatTaskRef();
  const dismiss = onDismiss ? (
    <Button variant="ghost" size="xs" onClick={onDismiss} data-hint="Leave the order as it is">
      <X />
      Dismiss
    </Button>
  ) : null;
  const actions = (
    <>
      {n && onAccept ? (
        <Button variant="outline" size="xs" onClick={onAccept} data-hint="Apply every change below in one go">
          <Check />
          Accept
        </Button>
      ) : null}
      {dismiss}
    </>
  );
  if (stale) {
    return (
      <AppAlert
        tone="warning"
        compact
        icon={<Sparkles className="size-4" />}
        data-testid="subtask-order"
        action={
          <span className="flex gap-1">
            {onSuggestAgain ? (
              <Button variant="outline" size="xs" loading={suggesting} onClick={onSuggestAgain} data-hint="The AI reads the sub-tasks as they are now">
                <RotateCw />
                Suggest again
              </Button>
            ) : null}
            {dismiss}
          </span>
        }
      >
        The sub-tasks changed since the AI suggested an order for them (or one it changes was built), so it can't be applied as it is.
      </AppAlert>
    );
  }
  return (
    <AppAlert
      tone="info"
      compact
      icon={<Sparkles className="size-4" />}
      data-testid="subtask-order"
      title={n ? `The AI suggests ${n === 1 ? 'a change' : `${n} changes`} to the order` : 'The AI suggests keeping the order as it is'}
      // Beside the text where there's room; under it on phones, so the text keeps the width.
      action={<span className="hidden gap-1 sm:flex">{actions}</span>}
    >
      {suggestion.summary ? <p>{suggestion.summary}</p> : null}
      {suggestion.order?.length ? <p className="mt-1">The list in this order: {suggestion.order.map((o) => formatRef(o.number)).join(', ')}</p> : null}
      {n ? <p className="mt-1">The rows below say what changes; nothing does until it's accepted.</p> : null}
      <span className="mt-2 flex gap-1 sm:hidden">{actions}</span>
    </AppAlert>
  );
}

/** SF-T-271: one row's part of the AI's suggested order: where it moves in the list, what it would wait on instead, its break, and why. */
function SuggestedOrder({ change, position }: { change?: SubtaskOrderChange; position?: number }) {
  const formatRef = useFormatTaskRef();
  const names = (after: SubtaskOrderChange['after']) => (after.length ? `after ${after.map((a) => formatRef(a.number)).join(', ')}` : 'after the one before');
  const was = new Set(change?.was_after.map((a) => a.task_id) ?? []);
  const afterChanged = Boolean(change) && (was.size !== change!.after.length || change!.after.some((a) => !was.has(a.task_id)));
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pb-1 text-xs" data-testid="subtask-suggested">
      <Badge variant="ai" size="sm">
        <Sparkles aria-hidden />
        Suggested
      </Badge>
      {position ? <span className="text-ai-strong">Moves to #{position}</span> : null}
      {change && afterChanged ? (
        <span>
          <span className="text-muted-foreground line-through">{names(change.was_after)}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="text-ai-strong">{names(change.after)}</span>
        </span>
      ) : null}
      {change && change.chain_break !== change.was_break ? (
        <span className="inline-flex items-center gap-1 text-ai-strong">
          <Hand className="size-3" aria-hidden />
          {change.chain_break ? 'Stop here for a person' : 'No stop here'}
        </span>
      ) : null}
      {change?.why ? <span className="basis-full text-muted-foreground">{change.why}</span> : null}
    </div>
  );
}

function SubtaskRow({
  task,
  columns,
  members,
  labels,
  types,
  canRun,
  sortable,
  picked,
  onPick,
  onToggleDone,
  onHandToAi,
  onToggleBoard,
  onPromote,
  siblings,
  unlocked,
  showAfter,
  onChangeAfter,
  onToggleBreak,
  stopped,
  suggested,
  suggestedAt,
}: {
  task: TaskCard;
  columns: TaskColumn[];
  members: TaskMember[];
  labels: TaskLabel[];
  types?: TaskType[];
  canRun: boolean;
  sortable: boolean;
  /** SF-T-268: the list, for its After… picker. */
  siblings: TaskCard[];
  /** SF-T-279: the sub-tasks that are ready for what waits on them (isSubtaskUnlocked): green in its 'after …'. */
  unlocked: ReadonlySet<string>;
  /**
   * SF-T-268: show 'after …' in its details: it names sub-tasks to wait on, other than only the one before it in the
   * list, which it waits on anyway.
   */
  showAfter: boolean;
  onChangeAfter?: (ids: string[], was?: string[]) => Promise<boolean>;
  /** SF-T-269: Stop here for a person, or not; and whether the chain stopped here. */
  onToggleBreak?: (on: boolean) => void;
  stopped: boolean;
  /** SF-T-271: what the AI's suggested order would change on it. */
  suggested?: SubtaskOrderChange;
  /** SF-T-271: where the AI's suggested order would move it in the list (1-based), when it moves it. */
  suggestedAt?: number;
  /** Whether it's picked for the run; null while not picking. */
  picked: boolean | null;
  onPick: (on: boolean) => void;
  onToggleDone: (done: boolean) => void;
  onHandToAi: () => void;
  onToggleBoard: () => void;
  onPromote?: () => void;
}) {
  const formatRef = useFormatTaskRef();
  const boardPath = useBoardPath();
  const location = useLocation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled: !sortable });
  const isDone = isSubtaskDone(task, columns);
  const column = columns.find((c) => c.id === task.column_id);
  const ref = formatRef(task.number);
  const withAi = waitsOnAi(task.waiting_on);
  const run = useTaskActiveRun(task.id);
  const [editingAfter, setEditingAfter] = useState(false);
  const editAfter = onChangeAfter && !isDone ? () => setEditingAfter(true) : undefined;
  const after = useAfterAutosave(task, siblings, onChangeAfter);
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('group/row relative flex min-h-9 items-start gap-2 bg-background px-2 py-1', sortable && 'pl-0.5', isDragging && 'z-10 rounded-md shadow-md')}
    >
      {run && !isDone ? <RunProgressBar run={run} variant="stripe" /> : null}
      {sortable ? (
        <button
          type="button"
          aria-label={`Move ${ref}`}
          data-hint="Drag to reorder"
          className="-mr-1 flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
      ) : null}
      <span className="flex h-7 shrink-0 items-center">
        <Checkbox checked={isDone} onCheckedChange={(checked) => onToggleDone(checked === true)} aria-label={isDone ? `Not done: ${ref}` : `Done: ${ref}`} />
      </span>
      <div className="min-w-0 flex-1">
        <Link
          to={boardPath(task.id)}
          // SF-T-134: closing it comes back here, to the parent.
          state={fromParentState(location.state)}
          className={cn('block py-1 text-sm leading-5 hover:underline', isDone ? 'truncate text-muted-foreground' : 'break-words')}
        >
          <span className="mr-1.5 text-xs tabular-nums text-muted-foreground">{ref}</span>
          <span className={cn(isDone && 'line-through')}>{task.title}</span>
        </Link>
        {/* Done ones stay one muted line. */}
        {isDone ? null : (
          <SubtaskDetails task={task} members={members} labels={labels} types={types} showAfter={showAfter} unlocked={unlocked} onEditAfter={editAfter} />
        )}
        {suggested || suggestedAt ? <SuggestedOrder change={suggested} position={suggestedAt} /> : null}
      </div>
      <Popover
        open={editingAfter}
        onOpenChange={(open) => {
          setEditingAfter(open);
          // Closing sends the ticks at once rather than after the pause.
          if (!open) void after.flush();
        }}
      >
        <PopoverAnchor asChild>
          <span className="flex h-7 shrink-0 items-center gap-2">
            {task.show_on_board ? (
              <LayoutDashboard className="size-3.5 shrink-0 text-muted-foreground" aria-label="On the Board too" data-hint="On the Board too" />
            ) : null}
            {task.parent?.orphaned ? <OrphanedBadge /> : null}
            {/* SF-T-263: picked for the one-at-a-time chain. */}
            {task.in_chain && !isDone ? (
              <StatusBadge tone="info" size="sm" data-hint="Picked for the chain: the AI builder builds the picked ones one at a time, then stops">
                In the chain
              </StatusBadge>
            ) : null}
            {/* SF-T-269: the chain stops here for a person, once it has its PR. */}
            {task.chain_break ? (
              <StatusBadge
                tone={task.chain_break_passed_at ? 'neutral' : 'warning'}
                size="sm"
                data-hint={
                  task.chain_break_passed_at
                    ? 'A break the chain went past (Continue): it no longer stops it. Set Stop here for a person again to stop here again'
                    : stopped
                      ? 'The chain stopped here for a person: press Continue above to go on'
                      : 'Stop here for a person: once this has its PR, the AI builder hands over nothing that waits on it until someone presses Continue'
                }
              >
                <Hand className="size-3" aria-hidden />
                {/* Phones: the hand only, so the title keeps its room. */}
                <span className="sr-only sm:not-sr-only">{task.chain_break_passed_at ? 'Passed' : stopped ? 'Stopped here' : 'Stop here'}</span>
              </StatusBadge>
            ) : null}
            {column && !isDone ? (
              // Phones leave it out: the title needs the room.
              <StatusBadge tone="neutral" size="sm" className="hidden sm:inline-flex">
                {column.name}
              </StatusBadge>
            ) : null}
            {/* SF-T-268: a light way in to After… on a row that shows none: on hover, where there's a pointer (phones use ⋯). */}
            {editAfter && !showAfter && picked === null ? (
              <button
                type="button"
                className="hidden rounded px-1 text-2xs text-muted-foreground opacity-0 group-hover/row:opacity-100 hover:text-foreground hover:underline focus-visible:opacity-100 [@media(hover:hover)]:inline-flex"
                onClick={editAfter}
                data-hint="Choose the sub-tasks it waits on"
              >
                After…
              </button>
            ) : null}
            {picked !== null && !isDone ? (
              <Checkbox checked={picked} onCheckedChange={(checked) => onPick(checked === true)} aria-label={`Pick ${ref} for the run`} />
            ) : (
              <SubtaskMenu
                task={task}
                canHand={canRun && !isDone && !withAi}
                onHandToAi={onHandToAi}
                onToggleBoard={onToggleBoard}
                onPromote={onPromote}
                onEditAfter={editAfter}
                onToggleBreak={onToggleBreak}
              />
            )}
          </span>
        </PopoverAnchor>
        {editingAfter && onChangeAfter ? (
          <SubtaskAfterPicker task={task} siblings={siblings} columns={columns} after={after}
            onClose={() => {
              setEditingAfter(false);
              void after.flush();
            }}
          />
        ) : null}
      </Popover>
    </li>
  );
}

/**
 * An open sub-task's details, from the Board card's pieces (TaskCardMeta): one line that wraps, left
 * out when there's nothing to show.
 */
function SubtaskDetails({
  task,
  members,
  labels,
  types,
  showAfter,
  unlocked,
  onEditAfter,
}: {
  task: TaskCard;
  members: TaskMember[];
  labels: TaskLabel[];
  types?: TaskType[];
  showAfter: boolean;
  unlocked: ReadonlySet<string>;
  onEditAfter?: () => void;
}) {
  const formatRef = useFormatTaskRef();
  const turn = useTaskTurn(task);
  const run = useTaskActiveRun(task.id);
  const { prs } = taskCardFields(task, types);
  const pickupSkip = pickupSkipOf(task.waiting_on);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-1 text-xs text-muted-foreground empty:hidden" data-testid="subtask-details">
      <TaskCardChips task={task} labels={labels} types={types} />
      {/* T-27: the AI builder made it on its own; it's never built until a person promotes it. */}
      {task.draft ? (
        <StatusBadge size="sm" tone="warning" data-hint="Made by the AI builder. Open it to promote or discard it; it isn't built until then.">
          Draft
        </StatusBadge>
      ) : null}
      {pickupSkip ? <PickupSkipBadge skip={pickupSkip} /> : null}
      {run?.limit_warning ? <RunLimitBadge warning={run.limit_warning} /> : null}
      {prs.length ? <TaskCardPrs taskId={task.id} prs={prs} parent={task.parent} waiting={task.waiting_on} /> : null}
      {showAfter ? <SubtaskAfter after={task.after} onEdit={onEditAfter} unlocked={(id) => unlocked.has(id)} /> : null}
      <TaskCardCounts task={task} turn={turn} />
      <TaskCardPeople task={task} members={members} turn={turn} formatRef={formatRef} />
    </div>
  );
}

function SubtaskMenu({
  task,
  canHand,
  onHandToAi,
  onToggleBoard,
  onPromote,
  onEditAfter,
  onToggleBreak,
}: {
  task: TaskCard;
  canHand: boolean;
  onHandToAi: () => void;
  onToggleBoard: () => void;
  onPromote?: () => void;
  /** SF-T-268: opens the row's After… picker. */
  onEditAfter?: () => void;
  onToggleBreak?: (on: boolean) => void;
}) {
  // Set by After… only: focus goes into the picker then; otherwise it goes back to the menu's button, as usual.
  const toPicker = useRef(false);
  return (
    <span className="flex shrink-0 items-center">
      {canHand ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onHandToAi}
          aria-label="Hand to the AI builder"
          data-hint="Hand to the AI builder: it builds this one in a run of its own"
        >
          <Bot className="size-4" />
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="More actions">
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        {/* The After… picker opens on the row: focus stays there rather than going back to this button. */}
        <DropdownMenuContent
          align="end"
          onCloseAutoFocus={(e) => {
            if (toPicker.current) e.preventDefault();
            toPicker.current = false;
          }}
        >
          {onEditAfter ? (
            <DropdownMenuItem
              onSelect={() => {
                toPicker.current = true;
                onEditAfter();
              }}
            >
              <GitFork className="size-4" />
              After…
            </DropdownMenuItem>
          ) : null}
          {onToggleBreak ? (
            <DropdownMenuCheckboxItem checked={task.chain_break} onCheckedChange={(on) => onToggleBreak(on === true)}>
              <Hand className="size-4" />
              Stop here for a person
            </DropdownMenuCheckboxItem>
          ) : null}
          <DropdownMenuItem onSelect={onToggleBoard}>
            {task.show_on_board ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            {task.show_on_board ? 'Hide from the Board' : 'Show on the Board too'}
          </DropdownMenuItem>
          {onPromote ? (
            <DropdownMenuItem onSelect={onPromote}>
              <ArrowUpFromLine className="size-4" />
              Make it a task of its own
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

/**
 * SF-T-144: a sub-task the server doesn't have yet. Saving: a spinner. Not saved (the save failed, or
 * the page closed before it finished): Retry and Remove. It stays here, kept in the browser, until then.
 */
function PendingRow({ row, onRetry, onRemove }: { row: PendingSubtaskRow; onRetry: () => void; onRemove: () => void }) {
  return (
    <li className="flex min-h-9 items-center gap-2 bg-background px-2 py-1" data-testid="pending-subtask">
      <span className="flex h-7 w-4 shrink-0 items-center justify-center">
        {row.saving ? <Spinner /> : <CloudOff className="size-4 text-warning-strong" aria-hidden />}
      </span>
      <span className={cn('min-w-0 flex-1 break-words py-1 text-sm leading-5', row.saving && 'text-muted-foreground')}>{row.title}</span>
      {row.saving ? (
        <span className="shrink-0 text-xs text-muted-foreground">Saving…</span>
      ) : (
        <span className="flex shrink-0 items-center gap-1">
          <StatusBadge tone="warning" size="sm" data-hint="The server doesn't have it yet. It's kept in this browser until it does.">
            Not saved
          </StatusBadge>
          <Button variant="ghost" size="icon-sm" onClick={onRetry} aria-label={`Retry saving ${row.title}`} data-hint="Retry">
            <RotateCw className="size-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={onRemove} aria-label={`Remove ${row.title}`} data-hint="Remove it">
            <X className="size-4" />
          </Button>
        </span>
      )}
    </li>
  );
}

/** SF-T-242: the sub-tasks header's settings: how the AI builder builds them. */
function SubtaskBuildMenu({
  build,
  onChange,
  chainLeft,
  onClearChain,
}: {
  build: SubtaskBuild;
  onChange: (change: Partial<SubtaskBuild>) => void;
  /** SF-T-263: picked sub-tasks still to build, 0 with no pick. */
  chainLeft: number;
  /** SF-T-263: there's a pick to clear. */
  onClearChain?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-xs" aria-label="Sub-task build settings" data-hint="How the AI builder builds these">
          <Settings2 />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>How the AI builder builds them</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={build.subtasks_one_at_a_time}
          onCheckedChange={(on) => onChange({ subtasks_one_at_a_time: on === true })}
          onSelect={(e) => e.preventDefault()}
        >
          <span className="flex flex-col">
            <span>One at a time</span>
            <span className="text-2xs text-muted-foreground">Each its own run and PR, stacked on the one before. The next starts once the last has a PR.</span>
            {/* SF-T-263: the chain Pick… set, until it's built. */}
            {chainLeft ? (
              <span className="text-2xs text-muted-foreground" data-testid="chain-left">
                {chainLeft === 1 ? '1 picked sub-task left in the chain' : `${chainLeft} picked sub-tasks left in the chain`}
              </span>
            ) : build.subtasks_one_at_a_time ? (
              <span className="text-2xs text-muted-foreground">No pick: every open sub-task. Pick… to build only some.</span>
            ) : null}
          </span>
        </DropdownMenuCheckboxItem>
        {/* SF-T-267: how many the AI builder has at once, one at a time. */}
        {build.subtasks_one_at_a_time && build.subtasks_concurrency !== undefined ? (
          <SubtaskConcurrency value={build.subtasks_concurrency} onChange={(n) => onChange({ subtasks_concurrency: n })} />
        ) : null}
        <DropdownMenuCheckboxItem
          checked={build.subtasks_defer_questions}
          onCheckedChange={(on) => onChange({ subtasks_defer_questions: on === true })}
          onSelect={(e) => e.preventDefault()}
        >
          <span className="flex flex-col">
            <span>Don&apos;t stop for questions</span>
            <span className="text-2xs text-muted-foreground">
              Its runs (not ones you start with Run) build on with their best call and go straight to the PR, without waiting for picks on their suggestions; the questions are listed for follow-up changes.
            </span>
          </span>
        </DropdownMenuCheckboxItem>
        {onClearChain ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onClearChain}>
              <X />
              <span className="flex flex-col">
                <span>Clear the pick</span>
                <span className="text-2xs text-muted-foreground">The AI builder hands no more over; one being built carries on. Build again for every open sub-task, or pick again.</span>
              </span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


/** SF-T-268: how long the After… picker waits for more ticks before saving them together. */
const AFTER_SAVE_DELAY_MS = 800;

interface AfterAutosave {
  /** The ids ticked: what it waits on, with the ticks not saved yet applied. */
  shown: string[];
  toggle: (id: string) => void;
  /** Saves the ticks now (closing the picker). */
  flush: () => Promise<void>;
  status: 'idle' | 'saving' | 'failed';
}

/**
 * SF-T-268: the After… picker's ticks, saved by themselves: bundled for a moment, then sent applied onto what the
 * sub-task waits on by then, so a change someone made meanwhile (live) stays and shows. Closing the picker sends them at
 * once. A failed save keeps them (Retry in the picker). Kept on the row, so they outlive the picker.
 */
function useAfterAutosave(task: TaskCard, siblings: TaskCard[], save?: (ids: string[], was?: string[]) => Promise<boolean>): AfterAutosave {
  // id → ticked (true) or unticked (false), for the ticks not saved yet.
  const [toggles, setToggles] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<AfterAutosave['status']>('idle');
  const current = task.after.map((d) => d.task_id);
  const shown = applyToggles(current, toggles, siblings);
  const pending = Object.keys(toggles).length > 0;

  // One save at a time (ticks made meanwhile go in the next), except `force`: the row is going, so it's now or never.
  const flush = useCallback(async (force = false) => {
    if (!save || !Object.keys(toggles).length || (status === 'saving' && !force)) return;
    const sent = toggles;
    setStatus('saving');
    // With what it was changed from, so the server writes only these ticks onto what it waits on by then (SF-T-268):
    // another person's change, or this picker's own save still in flight, isn't undone.
    const was = task.after.map((d) => d.task_id);
    const ok = await save(applyToggles(was, sent, siblings), was);
    // Ticks made while it was saving stay, for the next save.
    if (ok) setToggles((prev) => Object.fromEntries(Object.entries(prev).filter(([id, on]) => sent[id] !== on)));
    setStatus(ok ? 'idle' : 'failed');
  }, [save, toggles, status, task.after, siblings]);

  useEffect(() => {
    if (!pending || status !== 'idle') return;
    const timer = window.setTimeout(() => void flush(), AFTER_SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [pending, status, flush]);

  // Leaving the page (the row goes) mid-pause still saves them.
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);
  useEffect(() => () => void flushRef.current(true), []);

  const toggle = (id: string) => {
    const on = !shown.includes(id);
    setToggles((prev) => {
      const next = { ...prev };
      // Back to what it waits on: nothing to save for it.
      if (on === current.includes(id)) delete next[id];
      else next[id] = on;
      return next;
    });
    if (status === 'failed') setStatus('idle');
  };

  return {
    shown,
    toggle,
    flush: async () => {
      if (status === 'failed') setStatus('idle');
      await flush();
    },
    status,
  };
}

/** What it waits on with the ticks applied, in the list's order. */
function applyToggles(current: string[], toggles: Record<string, boolean>, siblings: TaskCard[]): string[] {
  const ids = new Set([...current.filter((id) => toggles[id] !== false), ...Object.keys(toggles).filter((id) => toggles[id])]);
  return siblings.filter((t) => ids.has(t.id)).map((t) => t.id);
}

/**
 * SF-T-268: a sub-task's After… picker: the other sub-tasks (not drafts, which can't be waited on; a done one only
 * while it's ticked), ticked for the ones it waits on, saved by themselves (useAfterAutosave). One that already waits on
 * it can't be ticked: that's a loop.
 */
function SubtaskAfterPicker({
  task,
  siblings,
  columns,
  after,
  onClose,
}: {
  task: TaskCard;
  siblings: TaskCard[];
  columns: TaskColumn[];
  after: AfterAutosave;
  onClose: () => void;
}) {
  const formatRef = useFormatTaskRef();
  const loops = waitingOn(siblings, task.id);
  const choices = siblings.filter((t) => t.id !== task.id && !t.draft && (!isSubtaskDone(t, columns) || after.shown.includes(t.id)));
  return (
    <PopoverContent
      align="end"
      className="w-80 max-w-[calc(100vw-1rem)] p-0"
      aria-label={`What ${formatRef(task.number)} waits on`}
      // Opened from the row's menu, whose closing moves focus: a click outside still closes it.
      onFocusOutside={(e) => e.preventDefault()}
    >
      <div className="border-b px-3 py-2">
        <p className="text-sm font-medium">{formatRef(task.number)} starts after…</p>
        <p className="text-2xs text-muted-foreground">Built one at a time, it waits until each of these has a PR or is done.</p>
      </div>
      {choices.length ? (
        <ul className="max-h-72 overflow-y-auto py-1">
          {choices.map((t) => {
            const loop = loops.has(t.id);
            const ref = formatRef(t.number);
            return (
              <li key={t.id}>
                {/* Phones: each row a 44px touch target (the task pop-up is a phone page). */}
                <label className={cn('flex min-h-11 items-start gap-2 px-3 py-2.5 text-sm sm:min-h-0 sm:py-1.5', loop ? 'text-muted-foreground' : 'cursor-pointer hover:bg-muted')}>
                  <span className="flex h-5 shrink-0 items-center">
                    <Checkbox checked={after.shown.includes(t.id)} disabled={loop} onCheckedChange={() => after.toggle(t.id)} aria-label={`After ${ref}`} />
                  </span>
                  <span className="min-w-0 flex-1 leading-5">
                    <span className="mr-1.5 text-xs tabular-nums text-muted-foreground">{ref}</span>
                    {t.title}
                    {loop ? <span className="block text-2xs">It waits on {formatRef(task.number)} already</span> : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-3 py-2 text-sm text-muted-foreground">No other open sub-tasks to wait on.</p>
      )}
      <div className="flex min-h-10 items-center justify-between gap-2 border-t px-3 py-1.5 text-2xs text-muted-foreground">
        {after.status === 'failed' ? (
          <span className="flex items-center gap-2 text-danger-strong">
            Not saved
            <Button variant="outline" size="xs" className="max-sm:h-11" onClick={() => void after.flush()}>
              <RotateCw />
              Retry
            </Button>
          </span>
        ) : after.status === 'saving' ? (
          <span className="flex items-center gap-1.5">
            <Spinner size="xs" />
            Saving…
          </span>
        ) : (
          <span>Ticks save by themselves.</span>
        )}
        <Button variant="ghost" size="xs" className="max-sm:h-11 max-sm:px-4" onClick={onClose}>
          Close
        </Button>
      </div>
    </PopoverContent>
  );
}

/**
 * SF-T-267: the settings menu's "At once": how many sub-tasks the AI builder has at once, one at a time, from 1 to
 * SUBTASK_CONCURRENCY_MAX. More than 1 lets sub-tasks that don't wait on each other (After…) build side by side.
 */
function SubtaskConcurrency({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-start gap-2 py-1.5 pr-2 pl-8 text-sm" data-testid="subtask-concurrency">
      <span className="flex min-w-0 flex-1 flex-col">
        <span id="subtask-concurrency-label">At once</span>
        <span className="text-2xs text-muted-foreground">
          {value === 1 ? 'One sub-task with the AI builder at a time.' : `Up to ${value} side by side, where they don’t wait on each other (After…).`}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1" role="group" aria-labelledby="subtask-concurrency-label">
        <Button variant="outline" size="icon-xs" className="max-sm:size-11" aria-label="One fewer at once" disabled={value <= 1} onClick={() => onChange(value - 1)}>
          <Minus />
        </Button>
        <span className="w-5 text-center tabular-nums" aria-live="polite">
          {value}
        </span>
        <Button
          variant="outline"
          size="icon-xs"
          className="max-sm:size-11"
          aria-label="One more at once"
          disabled={value >= SUBTASK_CONCURRENCY_MAX}
          disabledReason={`At most ${SUBTASK_CONCURRENCY_MAX}`}
          onClick={() => onChange(value + 1)}
        >
          <Plus />
        </Button>
      </span>
    </div>
  );
}
